import { useEffect, useState } from "react";
import {
    Eye, X, Search, ArrowLeft, User, AlertCircle,
    Edit, Save, Tag, Users, FileText,
    MapPin, UserCheck, Clock,
} from "lucide-react";
import Swal from "sweetalert2";
import config from "../../config";

export default function OpenTickets({ handlePageChange }) {
    const [tickets, setTickets] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [search, setSearch] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [filterPriority, setFilterPriority] = useState("All");
    const [currentUser, setCurrentUser] = useState(null);
    const [now, setNow] = useState(new Date());
    const [editForm, setEditForm] = useState({
        assignees: "", status: "", priority: "", notes: "",
        tags: "", origin: "", calendar: "", timeGoal: "", team: "",
    });

    const API_TICKETS = `${config.API_BASE_URL}/Tickets`;
    const API_ADMINS = `${config.API_BASE_URL}/Admin`;
    const API_SLA = `${config.API_BASE_URL}/SLA`;

    const originOptions = ["Email", "Phone", "Chat", "Web Portal", "Walk-in", "Social Media"];
    const teamOptions = ["IT Support", "Network Team", "Development", "Infrastructure", "Security", "Help Desk"];
    const timeGoalOptions = ["1 hour", "2 hours", "4 hours", "8 hours", "1 day", "2 days", "3 days", "1 week"];
    const calendarOptions = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    // Get current logged-in user
    useEffect(() => {
        try {
            const raw =
                localStorage.getItem("admin") || sessionStorage.getItem("admin") ||
                localStorage.getItem("user") || sessionStorage.getItem("user");
            if (raw) {
                const parsed = JSON.parse(raw);
                setCurrentUser(parsed);
            }
        } catch (err) {
            console.error("Failed to parse user from storage", err);
        }
    }, []);

    const loadTickets = async () => {
        try {
            const res = await fetch(API_TICKETS);
            if (!res.ok) throw new Error();
            const data = await res.json();
            setTickets(data.filter((t) => t.status === "Open"));
        } catch {
            Swal.fire("Error", "Failed to load tickets", "error");
        }
    };

    const loadAdmins = async () => {
        try {
            const res = await fetch(API_ADMINS);
            if (!res.ok) throw new Error();
            setAdmins(await res.json());
        } catch {
            Swal.fire("Error", "Failed to load admins", "error");
        }
    };

    useEffect(() => { loadTickets(); loadAdmins(); }, []);

    // ✅ Realtime clock — updates every second
    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);
    // ✅ Fetch SLA by priority
    const getSLAByPriority = async (priority) => {
        try {
            const res = await fetch(API_SLA);
            if (!res.ok) return null;
            const slaList = await res.json();
            return slaList.find(
                (s) => s.priority?.toLowerCase() === priority?.toLowerCase()
            ) || null;
        } catch {
            return null;
        }
    };

    // ✅ Calculate deadline from SLA calendar field
    const calcDeadlineFromSLA = (sla) => {
        const now = new Date();
        if (!sla?.calendar) {
            now.setDate(now.getDate() + 7); // fallback
            return now.toISOString();
        }
        const cal = sla.calendar.toLowerCase();

        if (cal.includes("hour")) {
            const hrs = parseInt(cal) || 4;
            now.setHours(now.getHours() + hrs);
        } else if (cal.includes("day")) {
            const days = parseInt(cal) || 1;
            now.setDate(now.getDate() + days);
        } else if (cal.includes("week")) {
            const weeks = parseInt(cal) || 1;
            now.setDate(now.getDate() + weeks * 7);
        } else {
            now.setDate(now.getDate() + 3); // fallback
        }
        return now.toISOString();
    };

    // ✅ ASSIGN TO ME — auto SLA, no reason input required
    const assignToMe = async (ticket) => {
        if (!currentUser) {
            Swal.fire({
                icon: "error", title: "Not Logged In",
                text: "No logged in user found.",
                background: "#1e293b", color: "#f1f5f9",
            });
            return;
        }

        // Block if ticket already has a pending request
        if (ticket.assignmentRequest && ticket.assignmentRequestApproved === null) {
            Swal.fire({
                icon: "warning", title: "Request Already Pending",
                html: `This ticket already has a pending assignment request from <b>${ticket.assignmentRequest}</b>.`,
                background: "#1e293b", color: "#f1f5f9",
            });
            return;
        }

        const assigneeName = currentUser.fullNames || currentUser.username;

        // Confirm dialog — simple, no reason field
        const { isConfirmed } = await Swal.fire({
            title: "Assign to Me",
            html: `
                <div style="text-align:left; font-size:13px; color:#94a3b8; line-height:1.9">
                    <div>🎫 Ticket: <strong style="color:#60a5fa">${ticket.ticketNumber}</strong></div>
                    <div>📋 Subject: <span style="color:#e2e8f0">${ticket.subject || ""}</span></div>
                    <div>👤 Assignee: <strong style="color:#4ade80">${assigneeName}</strong></div>
                    <div style="margin-top:10px; padding:8px 12px; background:#0f172a; border-radius:8px; border:1px solid #334155">
                        🕐 Deadline & time goal will be auto-calculated based on 
                        <strong style="color:#f59e0b">${ticket.priority || "Medium"}</strong> priority SLA.
                    </div>
                    <div style="margin-top:10px; color:#f59e0b; font-size:12px">
                        ⏳ This will send a request to the admin for approval.
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: "📨 Submit Request",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#3b82f6",
            cancelButtonColor: "#64748b",
            background: "#1e293b",
            color: "#f1f5f9",
            width: "480px",
        });

        if (!isConfirmed) return;

        // ✅ Fetch SLA for the ticket's priority
        const sla = await getSLAByPriority(ticket.priority);

        try {
            const res = await fetch(`${API_TICKETS}/${ticket.ticketNumber}/assign-to-me`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    assigneeName: assigneeName,
                    reason: `Auto-assigned via Assign to Me. SLA Priority: ${ticket.priority}`,
                    // Pass SLA-derived values so backend can use them on approval
                    deadline: sla ? calcDeadlineFromSLA(sla) : null,
                    timeGoal: sla?.timeGoal || null,
                    calendar: sla?.calendar || null,
                    jql: sla?.jql || null,
                    condition: sla?.condition || null,
                }),
            });

            if (!res.ok) throw new Error();

            // Build deadline label for success message
            const deadlineDate = sla
                ? new Date(calcDeadlineFromSLA(sla)).toLocaleString("en-US", {
                    month: "short", day: "numeric", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                })
                : "Auto-calculated";

            Swal.fire({
                icon: "info",
                title: "Request Submitted!",
                html: `
                    <div style="text-align:left; font-size:13px; color:#94a3b8; line-height:1.9">
                        <div>🎫 <strong style="color:#60a5fa">${ticket.ticketNumber}</strong></div>
                        <div>👤 Requested by: <strong style="color:#4ade80">${assigneeName}</strong></div>
                        ${sla ? `
                        <div style="margin-top:8px; padding:8px 12px; background:#0f172a; border-radius:8px; border:1px solid #334155">
                            <div>⏱ Time Goal: <strong style="color:#60a5fa">${sla.timeGoal}</strong></div>
                            <div>📅 SLA Calendar: <strong style="color:#60a5fa">${sla.calendar}</strong></div>
                            <div>📌 Est. Deadline: <strong style="color:#a78bfa">${deadlineDate}</strong></div>
                        </div>` : ""}
                        <div style="margin-top:10px; color:#f59e0b; font-size:12px">
                            ⏳ Waiting for admin approval.
                        </div>
                    </div>
                `,
                timer: 4000,
                showConfirmButton: false,
                background: "#1e293b",
                color: "#f1f5f9",
            });

            setShowModal(false);
            setSelectedTicket(null);
            setIsEditing(false);
            loadTickets();
        } catch {
            Swal.fire({
                icon: "error", title: "Failed",
                text: "Could not submit request. Please try again.",
                background: "#1e293b", color: "#f1f5f9",
            });
        }
    };

    const openViewModal = (ticket) => {
        setSelectedTicket(ticket);
        setEditForm({
            assignees: ticket.assignees || "",
            status: ticket.status || "Open",
            priority: ticket.priority || "Medium",
            notes: ticket.notes || "",
            tags: ticket.tags || "",
            origin: ticket.origin || "",
            calendar: ticket.calendar || "",
            timeGoal: ticket.timeGoal || "",
            team: ticket.team || "",
        });
        setIsEditing(false);
        setShowModal(true);
    };

    const saveChanges = async () => {
        try {
            const updatedTicket = { ...selectedTicket, ...editForm };
            const res = await fetch(`${API_TICKETS}/${selectedTicket.ticketNumber}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(updatedTicket),
            });
            if (!res.ok) throw new Error();
            Swal.fire({ icon: "success", title: "Updated!", timer: 1200, showConfirmButton: false, background: "#1e293b", color: "#f1f5f9" });
            setIsEditing(false);
            setSelectedTicket(updatedTicket);
            loadTickets();
        } catch {
            Swal.fire("Error", "Failed to update ticket", "error");
        }
    };

    const closeModal = () => { setShowModal(false); setSelectedTicket(null); setIsEditing(false); };
    const formatDate = (d) => d ? new Date(d).toLocaleString() : "N/A";

    // ✅ Format seconds into Xh Xm Xs
    const formatDuration = (seconds) => {
        if (seconds <= 0) return "00:00:00";
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    };

    // ✅ TimeSpent — counts up from startedAt
    const getTimeSpent = (ticket) => {
        if (!ticket.StartedAt) return "—";
        const diff = Math.floor((now - new Date(ticket.StartedAt)) / 1000);
        return formatDuration(diff);
    };

    // ✅ TimeRemaining — counts down to deadline
    const getTimeRemaining = (ticket) => {
        if (!ticket.deadline) return "—";
        const diff = Math.floor((new Date(ticket.deadline) - now) / 1000);
        if (diff <= 0) return <span className="text-red-400 font-bold">⚠ Overdue</span>;
        return <span className={diff < 3600 ? "text-red-400 font-semibold" : diff < 86400 ? "text-yellow-400" : "text-green-400"}>{formatDuration(diff)}</span>;
    };

    // ✅ Start / Mark In Progress
    const startTicket = async (ticket) => {
        const { isConfirmed } = await Swal.fire({
            title: "Start Ticket?",
            html: `<div style="font-size:13px;color:#94a3b8">
            🎫 <strong style="color:#60a5fa">${ticket.ticketNumber}</strong><br/>
            Status will change to <strong style="color:#60a5fa">Work In</strong> and timer starts now.
        </div>`,
            showCancelButton: true,
            confirmButtonText: "▶ Start",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#3b82f6",
            cancelButtonColor: "#64748b",
            background: "#1e293b",
            color: "#f1f5f9",
        });
        if (!isConfirmed) return;

        try {
            const res = await fetch(`${API_TICKETS}/${ticket.ticketNumber}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...ticket,
                    status: "Pending",
                    StartedAt: new Date().toISOString(),
                }),
            });
            if (!res.ok) throw new Error();
            Swal.fire({ icon: "success", title: "Started!", text: "Timer is now running.", timer: 1500, showConfirmButton: false, background: "#1e293b", color: "#f1f5f9" });
            loadTickets();
        } catch {
            Swal.fire({ icon: "error", title: "Failed", text: "Could not start ticket.", background: "#1e293b", color: "#f1f5f9" });
        }
    };
    const getStatusColor = (status) => ({
        Open: "bg-blue-500/20 text-blue-400 border-blue-500/30",
        Pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
        "In Progress": "bg-blue-500/20 text-blue-400 border-blue-500/30",
        Resolved: "bg-green-500/20 text-green-400 border-green-500/30",
        Closed: "bg-slate-500/20 text-slate-400 border-slate-500/30",
    }[status] || "bg-slate-500/20 text-slate-400 border-slate-500/30");

    const getPriorityColor = (priority) => ({
        Low: "bg-green-500/20 text-green-400 border-green-500/30",
        Medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
        High: "bg-orange-500/20 text-orange-400 border-orange-500/30",
        Critical: "bg-red-500/20 text-red-400 border-red-500/30",
    }[priority] || "bg-slate-500/20 text-slate-400 border-slate-500/30");

    const hasPendingRequest = (ticket) =>
        ticket.assignmentRequest && ticket.assignmentRequestApproved === null;

const isSuperAdmin = currentUser?.role?.toLowerCase().includes("super");
const isAdmin = currentUser?.role?.toLowerCase().includes("admin");
const myName = currentUser?.fullNames || currentUser?.username; // ✅ add this line

const visibleTickets = (isSuperAdmin || isAdmin)
    ? tickets
    : tickets.filter(t => {
        const isUnassigned = !t.assignees || t.assignees.trim() === "" || t.assignees.toLowerCase() === "unassigned";
        return isUnassigned || t.assignees === myName;
    });

    const openCount = visibleTickets.length;
    const assignedCount = visibleTickets.filter((t) => t.assignees && t.assignees.toLowerCase() !== "unassigned").length;
    const unassignedCount = visibleTickets.filter((t) => !t.assignees || t.assignees.toLowerCase() === "unassigned").length;

    const filteredTickets = visibleTickets.filter((ticket) => {
        const matchesSearch =
            ticket.ticketNumber?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.subject?.toLowerCase().includes(search.toLowerCase()) ||
            ticket.name?.toLowerCase().includes(search.toLowerCase());
        const matchesPriority = filterPriority === "All" || ticket.priority === filterPriority;
        return matchesSearch && matchesPriority;
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100 p-6">
            <div className="max-w-8xl mx-auto">

                {/* HEADER */}
                <div className="flex justify-between items-center mb-8">
                    <div className="flex items-center gap-4">
                        <button onClick={() => handlePageChange("dashboard")} className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 border border-slate-700/50 transition-all">
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Open Tickets</h1>
                            <p className="text-slate-400 text-sm mt-1">View open tickets and request assignment</p>
                        </div>
                    </div>
                    {currentUser && (
                        <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/60 border border-slate-700/50 rounded-xl text-sm">
                            <User size={16} className="text-green-400" />
                            <span className="text-slate-300">Logged in as:</span>
                            <span className="text-green-400 font-semibold">{currentUser.fullNames || currentUser.username}</span>
                        </div>
                    )}
                </div>

                {/* STATS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-slate-800/40 border border-blue-500/20 rounded-2xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-blue-500/20 rounded-xl"><FileText className="text-blue-400" size={22} /></div>
                        <div><p className="text-slate-400 text-sm">Open</p><p className="text-2xl font-bold text-blue-400">{openCount}</p></div>
                    </div>
                    <div className="bg-slate-800/40 border border-green-500/20 rounded-2xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-green-500/20 rounded-xl"><UserCheck className="text-green-400" size={22} /></div>
                        <div><p className="text-slate-400 text-sm">Assigned</p><p className="text-2xl font-bold text-green-400">{assignedCount}</p></div>
                    </div>
                    <div className="bg-slate-800/40 border border-red-500/20 rounded-2xl p-4 flex items-center gap-4">
                        <div className="p-3 bg-red-500/20 rounded-xl"><User className="text-red-400" size={22} /></div>
                        <div><p className="text-slate-400 text-sm">Unassigned</p><p className="text-2xl font-bold text-red-400">{unassignedCount}</p></div>
                    </div>
                </div>

                {/* FILTERS */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="relative">
                        <Search className="absolute left-4 top-3.5 text-slate-400" size={18} />
                        <input className="w-full pl-12 pr-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" placeholder="Search by ticket #, subject, or name..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <select className="px-4 py-3 bg-slate-800/60 border border-slate-700/50 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
                        <option value="All">All Priority</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Critical">Critical</option>
                    </select>
                </div>

                {/* TABLE */}
                <div className="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 overflow-hidden shadow-2xl">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-800/80 border-b border-slate-700/50">
                                <tr>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Ticket #</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Subject</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Requester</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Assignee</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Request Status</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">Priority</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">Status</th>
                                    <th className="p-4 text-left text-sm font-semibold text-slate-300">Created</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">⏱ Time Spent</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">⏳ Remaining</th>
                                    <th className="p-4 text-center text-sm font-semibold text-slate-300">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTickets.map((ticket) => (
                                    <tr key={ticket.ticketNumber} className={`border-b border-slate-700/30 hover:bg-slate-700/30 transition-colors ${hasPendingRequest(ticket) ? "bg-yellow-500/5" : ""}`}>
                                        <td className="p-4"><span className="font-mono text-blue-400 font-medium">{ticket.ticketNumber}</span></td>
                                        <td className="p-4 max-w-xs">
                                            <div className="truncate font-medium">{ticket.subject}</div>
                                            <div className="text-xs text-slate-400 truncate">{ticket.category}</div>
                                        </td>
                                        <td className="p-4">
                                            <div className="font-medium">{ticket.name}</div>
                                            <div className="text-xs text-slate-400">{ticket.email}</div>
                                        </td>
                                        <td className="p-4">
                                            {ticket.assignees
                                                ? <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium">{ticket.assignees}</span>
                                                : <span className="text-slate-500 text-xs italic">Unassigned</span>}
                                        </td>
                                        <td className="p-4">
                                            {ticket.assignmentRequest ? (
                                                <div className="flex flex-col gap-1">
                                                    <span className="text-xs text-slate-400">By: <span className="text-slate-200 font-medium">{ticket.assignmentRequest}</span></span>
                                                    {ticket.assignmentRequestApproved === null && (
                                                        <span className="flex items-center gap-1 text-xs text-yellow-400">
                                                            <Clock size={11} /> Pending Approval
                                                        </span>
                                                    )}
                                                    {ticket.assignmentRequestApproved === true && (
                                                        <span className="text-xs text-green-400">✓ Approved</span>
                                                    )}
                                                    {ticket.assignmentRequestApproved === false && (
                                                        <span className="text-xs text-red-400">✗ Rejected</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-slate-600 text-xs italic">—</span>
                                            )}
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>{ticket.priority || "N/A"}</span>
                                        </td>
                                        <td className="p-4 text-center">
                                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(ticket.status)}`}>{ticket.status || "N/A"}</span>
                                        </td>
                                        <td className="p-4 text-sm text-slate-300">{formatDate(ticket.createdAt)}</td>
                                        <td className="p-4 text-center font-mono text-sm text-blue-300">
                                            {getTimeSpent(ticket)}
                                        </td>
                                        <td className="p-4 text-center font-mono text-sm">
                                            {getTimeRemaining(ticket)}
                                        </td>
                                        <td className="p-4 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                {ticket.status !== "Pending" && (
                                                    <button
                                                        onClick={() => startTicket(ticket)}
                                                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border transition-all text-xs font-medium bg-green-500/10 text-green-400 hover:bg-green-500/20 border-green-500/20 cursor-pointer"
                                                        title="Start ticket"
                                                    >
                                                        ▶ Start
                                                    </button>
                                                )}
                                                {ticket.status === "Pending" && (
                                                    <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-xs font-medium bg-blue-500/10 text-blue-400 border-blue-500/20">
                                                        🔵 Work In
                                                    </span>
                                                )}
                                                <button
                                                    onClick={() => assignToMe(ticket)}
                                                    disabled={hasPendingRequest(ticket)}
                                                    className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border transition-all text-xs font-medium
                                                        ${hasPendingRequest(ticket)
                                                            ? "bg-slate-700/30 text-slate-500 border-slate-700 cursor-not-allowed"
                                                            : "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 border-blue-500/20 cursor-pointer"
                                                        }`}
                                                    title={hasPendingRequest(ticket) ? "Pending approval from admin" : "Assign this ticket to me"}
                                                >
                                                    {hasPendingRequest(ticket)
                                                        ? <><Clock size={13} /> Pending</>
                                                        : <><UserCheck size={13} /> Assign to Me</>
                                                    }
                                                </button>
                                                <button onClick={() => openViewModal(ticket)} className="p-2 rounded-lg bg-slate-500/10 text-slate-400 hover:bg-slate-500/20 border border-slate-500/20 transition-all" title="View">
                                                    <Eye size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredTickets.length === 0 && (
                                    <tr><td colSpan="9" className="text-center p-12 text-slate-400">
                                        <FileText size={48} className="mx-auto mb-3 opacity-30" />
                                        <p className="text-lg font-medium">No open tickets found</p>
                                        <p className="text-sm mt-1 opacity-60">All tickets have been handled or none exist yet.</p>
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* VIEW/EDIT MODAL */}
            {showModal && selectedTicket && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto relative shadow-2xl border border-slate-700/50">
                        <div className="sticky top-0 bg-slate-800 border-b border-slate-700/50 p-6 flex justify-between items-center z-10">
                            <div>
                                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Ticket Details</h2>
                                <p className="text-slate-400 text-sm mt-1">{selectedTicket.ticketNumber} • {selectedTicket.subject}</p>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    onClick={() => assignToMe(selectedTicket)}
                                    disabled={hasPendingRequest(selectedTicket)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm font-medium
                                        ${hasPendingRequest(selectedTicket)
                                            ? "bg-slate-700/30 text-slate-500 border-slate-700 cursor-not-allowed"
                                            : "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border-blue-500/20"
                                        }`}
                                >
                                    {hasPendingRequest(selectedTicket)
                                        ? <><Clock size={15} /> Pending Approval</>
                                        : <><UserCheck size={15} /> Request Assignment</>
                                    }
                                </button>
                                {!isEditing
                                    ? <button onClick={() => setIsEditing(true)} className="flex items-center gap-2 px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 border border-yellow-500/20 transition-all text-sm"><Edit size={16} /> Edit</button>
                                    : <button onClick={saveChanges} className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 border border-green-500/20 transition-all text-sm"><Save size={16} /> Save Changes</button>
                                }
                                <button onClick={closeModal} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-all"><X size={20} /></button>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">

                            {selectedTicket.assignmentRequest && (
                                <div className={`p-4 rounded-xl border flex items-center gap-3 ${selectedTicket.assignmentRequestApproved === true ? "bg-green-500/10 border-green-500/30" :
                                    selectedTicket.assignmentRequestApproved === false ? "bg-red-500/10 border-red-500/30" :
                                        "bg-yellow-500/10 border-yellow-500/30"}`}
                                >
                                    <Clock size={18} className={
                                        selectedTicket.assignmentRequestApproved === true ? "text-green-400" :
                                            selectedTicket.assignmentRequestApproved === false ? "text-red-400" : "text-yellow-400"
                                    } />
                                    <div>
                                        <p className="text-sm font-medium text-slate-200">
                                            Assignment Request by <span className="font-bold">{selectedTicket.assignmentRequest}</span>
                                        </p>
                                        <p className={`text-xs mt-0.5 ${selectedTicket.assignmentRequestApproved === true ? "text-green-400" :
                                            selectedTicket.assignmentRequestApproved === false ? "text-red-400" : "text-yellow-400"}`}
                                        >
                                            {selectedTicket.assignmentRequestApproved === true ? "✓ Approved — this person has been assigned" :
                                                selectedTicket.assignmentRequestApproved === false ? "✗ Rejected by admin" :
                                                    "⏳ Waiting for admin approval..."}
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><Tag className="inline mr-2" size={16} />Tags</label>
                                    {isEditing
                                        ? <input type="text" className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} placeholder="Enter tags" />
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">{selectedTicket.tags || <span className="text-slate-500 italic">No tags</span>}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><Users className="inline mr-2" size={16} />Team</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" value={editForm.team} onChange={(e) => setEditForm({ ...editForm, team: e.target.value })}>
                                            <option value="">Select a team</option>
                                            {teamOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">{selectedTicket.team || <span className="text-slate-500 italic">No team</span>}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><User className="inline mr-2" size={16} />Assignees</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" value={editForm.assignees} onChange={(e) => setEditForm({ ...editForm, assignees: e.target.value })}>
                                            <option value="">Select Assignee</option>
                                            {admins.map((a) => <option key={a.username} value={a.fullNames}>{a.fullNames} ({a.role})</option>)}
                                        </select>
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">
                                            {selectedTicket.assignees ? <span className="text-green-400 font-medium">{selectedTicket.assignees}</span> : <span className="text-slate-500 italic">Unassigned</span>}
                                        </div>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><MapPin className="inline mr-2" size={16} />Origin</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" value={editForm.origin} onChange={(e) => setEditForm({ ...editForm, origin: e.target.value })}>
                                            <option value="">Select an Origin</option>
                                            {originOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                                        </select>
                                        : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700">{selectedTicket.origin || <span className="text-slate-500 italic">Not specified</span>}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><AlertCircle className="inline mr-2" size={16} />Status</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                                            <option value="Open">Open</option>
                                            <option value="Pending">Pending</option>
                                            <option value="In Progress">In Progress</option>
                                            <option value="Resolved">Resolved</option>
                                            <option value="Closed">Closed</option>
                                        </select>
                                        : <div className={`px-4 py-2.5 rounded-xl border ${getStatusColor(selectedTicket.status)} text-center font-medium`}>{selectedTicket.status}</div>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2"><AlertCircle className="inline mr-2" size={16} />Priority</label>
                                    {isEditing
                                        ? <select className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" value={editForm.priority} onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}>
                                            <option value="Low">Low</option>
                                            <option value="Medium">Medium</option>
                                            <option value="High">High</option>
                                            <option value="Critical">Critical</option>
                                        </select>
                                        : <div className={`px-4 py-2.5 rounded-xl border ${getPriorityColor(selectedTicket.priority)} text-center font-medium`}>{selectedTicket.priority}</div>}
                                </div>
                            </div>

                            <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">
                                <label className="block text-sm font-medium text-slate-300 mb-2"><FileText className="inline mr-2" size={16} />Notes</label>
                                {isEditing
                                    ? <textarea className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50" rows="4" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Add notes..." />
                                    : <div className="px-4 py-2.5 bg-slate-800 rounded-xl border border-slate-700 min-h-[100px] whitespace-pre-wrap">{selectedTicket.notes || <span className="text-slate-500 italic">No notes</span>}</div>}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg text-slate-200 mb-3">Requester Info</h3>
                                    <InfoRow label="Name" value={selectedTicket.name} />
                                    <InfoRow label="Email" value={selectedTicket.email} />
                                    <InfoRow label="Contact" value={selectedTicket.contact} />
                                </div>
                                <div className="space-y-3">
                                    <h3 className="font-semibold text-lg text-slate-200 mb-3">Ticket Info</h3>
                                    <InfoRow label="Category" value={selectedTicket.category} />
                                    <InfoRow label="Created" value={formatDate(selectedTicket.createdAt)} />
                                    <InfoRow label="Deadline" value={formatDate(selectedTicket.deadline)} />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <h3 className="font-semibold text-lg text-slate-200 mb-2">Subject</h3>
                                    <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50">{selectedTicket.subject}</div>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-lg text-slate-200 mb-2">Description</h3>
                                    <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/50 max-h-40 overflow-y-auto whitespace-pre-wrap">
                                        {selectedTicket.description || <span className="text-slate-500 italic">No description</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function InfoRow({ label, value }) {
    return (
        <div>
            <label className="text-sm text-slate-400 mb-1 block">{label}</label>
            <div className="px-3 py-2 bg-slate-900/50 rounded-lg border border-slate-700/50 text-sm">
                {value || <span className="text-slate-600 italic">N/A</span>}
            </div>
        </div>
    );
}